import type { InspectOptions } from "node:util";

type LogParms = [message: unknown, ...optionalParams: unknown[]];

/**
 * Vercel only supports 3 levels of logging. We're adding additional DEBUG level.
 * https://vercel.com/docs/observability/runtime-logs#level
 *
 * ERROR - Fatal for a particular request. Should be fixed sooner than later.
 *
 * WARN - A note on something that should probably be looked at eventually.
 *
 * LOG - Detail on regular operation.
 *
 * DEBUG - Debug only info as well as time and timeEnd functions.
 */
const LogLevel = {
	DEBUG: 0,
	LOG: 1,
	WARN: 2,
	ERROR: 3,
} as const;
type LogLevel = keyof typeof LogLevel;

const strLogLevel = (process.env.NEXT_PUBLIC_LOG_LEVEL || "LOG") as LogLevel;
const valueLogLevel = LogLevel[strLogLevel];

const RESET = "\x1b[0m";
const BLUE = "\x1b[34m";
const GREEN = "\x1b[32m";
const YELLOW = "\x1b[33m";
const RED = "\x1b[31m";

const TIME = `⏱️`;
const DEBUG = `🐛`;
const OK = `✔️`;
const WARN = `⚠️`;
const ERROR = `❌`;

const PREFIX_TIME = `${TIME} `;
const PREFIX_DEBUG = `${BLUE}${DEBUG}${RESET} `;
const PREFIX_OK = `${GREEN}${OK}${RESET} `;
const PREFIX_WARN = `${YELLOW}${WARN}${RESET} `;
const PREFIX_ERROR = `${RED}${ERROR}${RESET} `;

export const getLogger = (groupLabel?: string) => {
	const PREFIX = groupLabel ? `[${groupLabel}] ` : "";
	return {
		getLogger(subGroupLabel: string) {
			return getLogger([groupLabel, subGroupLabel].filter(Boolean).join(" > "));
		},
		time(label: string) {
			if (valueLogLevel > LogLevel.DEBUG) return;
			console.time([PREFIX_TIME, PREFIX, label].filter(Boolean).join(" "));
		},
		timeEnd(label: string) {
			if (valueLogLevel > LogLevel.DEBUG) return;
			console.timeEnd([PREFIX_TIME, PREFIX, label].filter(Boolean).join(" "));
		},
		debug(...args: LogParms) {
			if (valueLogLevel > LogLevel.DEBUG) return;
			console.log(...[PREFIX_DEBUG, PREFIX, ...args].filter(Boolean));
		},
		log(...args: LogParms) {
			if (valueLogLevel > LogLevel.LOG) return;
			console.log(...[PREFIX_OK, PREFIX, ...args].filter(Boolean));
		},
		dir(item?: unknown, options?: InspectOptions) {
			if (valueLogLevel > LogLevel.LOG) return;
			console.dir(item, options);
		},
		warn(...args: LogParms) {
			if (valueLogLevel > LogLevel.WARN) return;
			console.warn(...[PREFIX_WARN, PREFIX, ...args].filter(Boolean));
		},
		error(...args: LogParms) {
			if (valueLogLevel > LogLevel.ERROR) return;
			console.error(...[PREFIX_ERROR, PREFIX, ...args].filter(Boolean));
		},
	};
};

export const logger = getLogger();
